import s from '@components/Tables/KeywordsTable/KeywordsTable.module.scss';
import { TextInput } from '@tremor/react';
import { CellContext } from '@tanstack/react-table';
import { FC, memo, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { findIndex } from 'lodash';
import { doc, updateDoc } from 'firebase/firestore';
import { TableKeyword } from './KeywordsTable';
import { useKeywordsStore } from '@/stores/keywordsStore';
import { db } from '@/firebase/init';
import { getWorkspaceKeywordsPath } from '@/utils/queryPaths';
import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import { EditIcon } from '@/assets';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';

interface Props {
  props: CellContext<TableKeyword, string>;
}

const keywordNameSchema = z.object({
  name: z.string().min(5),
});

type KeywordName = z.infer<typeof keywordNameSchema>;

export const KeywordCellElement: FC<Props> = memo(function KeywordCellElement({ props }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<KeywordName>({
    resolver: zodResolver(keywordNameSchema),
    defaultValues: { name: props.getValue() },
  });
  const workspaceId = useWorkspaceIdFromParams() as string;
  const setKeywords = useKeywordsStore((state) => state.setKeywords);
  const [isEditing, setIsEditing] = useState(false);
  const keywords = useKeywordsStore((state) => state.keywords);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { ref, ...rest } = register('name');

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const handleIsEditingChange = () => {
    if (isEditing) {
      formRef.current?.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true }),
      );
    } else {
      setIsEditing(true);
    }
  };

  const onSubmit = async (data: KeywordName) => {
    if (!isEditing) return;
    if (data.name === props.getValue()) {
      setIsEditing(false);
      return;
    }
    setIsLoading(true);
    const keyWordId = props.row.getValue('id') as string;
    const index = findIndex(keywords, { id: keyWordId });
    const keywordsCopy = [...keywords];
    const editedKeyword = {
      keyword: data.name,
      tags: keywordsCopy[index].tags,
      language: keywordsCopy[index].language,
    };
    try {
      await updateDoc(
        doc(db, getWorkspaceKeywordsPath(workspaceId, 'dataforseo'), keyWordId),
        editedKeyword,
      );
      setKeywords((keywords) => {
        const index = findIndex(keywords, { id: keyWordId });
        // copies array without replacing 'empty' with undefined
        const keywordsCopy = Array.prototype.slice.call(keywords);
        keywordsCopy[index] = { ...keywordsCopy[index], keyword: data.name };
        return keywordsCopy;
      });
      setIsEditing(false);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      className={s.keywordCellForm}
      onBlur={(e) => {
        const currentTarget = e.currentTarget;

        // Give browser time to focus the next element
        requestAnimationFrame(() => {
          // Check if the new focused element is a child of the original container
          if (!currentTarget.contains(document.activeElement)) {
            formRef.current?.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            );
          }
        });
      }}
      ref={formRef}
      onSubmit={(event) => {
        handleSubmit(onSubmit)(event);
      }}
    >
      <section className={s.keywordCell}>
        {isEditing ? (
          <TextInput
            disabled={isLoading}
            error={!!errors.name?.message}
            errorMessage={errors.name?.message}
            {...rest}
            className={s.keywordCellInput}
            ref={(rf) => {
              ref(rf);
              inputRef.current = rf;
            }}
          />
        ) : (
          <span className={s.keywordCellText}>{props.getValue()}</span>
        )}
      </section>
      <OutlinedButton
        type="button"
        disabled={isLoading}
        style={{ marginRight: '60px' }}
        icon={EditIcon}
        iconAltText="edit icon"
        onClick={handleIsEditingChange}
      >
        Edit
      </OutlinedButton>
    </form>
  );
});
